import { c as _c } from "react/compiler-runtime";
import { loader } from '@monaco-editor/react';
import type { LanguageDefinitionResponse } from '@onefootprint/request-types/dashboard';
import { useQuery } from '@tanstack/react-query';
import type { editor } from 'monaco-editor';
import { createContext, type ReactNode, useContext } from 'react';
import useStore from '@/playbook-builder/hooks/use-store';
import { defineCustomTheme, editorOptions } from './utils/monaco-themes';
import { registerLanguage } from './utils/register-language';

// Monaco Editor loads from cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs
// See CSP documentation in middleware.page.ts for security considerations

export const initMonaco = async (languageDefinition: LanguageDefinitionResponse) => {
  const monaco = await loader.init();
  registerLanguage(monaco, languageDefinition);
  defineCustomTheme(monaco);
  return monaco;
};
type MonacoContextType = {
  monaco: typeof import('monaco-editor') | null;
  isLoading: boolean;
  error: Error | null;
  editorOptions: editor.IStandaloneEditorConstructionOptions;
  languageSpec: LanguageDefinitionResponse;
};
export const emptyLanguageSpec: LanguageDefinitionResponse = {
  languageName: 'fp-rules-default',
  keywords: [],
  operatorMap: {},
  featureSets: []
};
const MonacoContext = createContext<MonacoContextType>({
  monaco: null,
  isLoading: true,
  error: null,
  editorOptions,
  languageSpec: emptyLanguageSpec
});
export const MonacoProvider = t0 => {
  const $ = _c(15);
  const {
    children,
    languageDefinition
  } = t0;
  const {
    data: t1
  } = useStore();
  const {
    canEdit
  } = t1;
  let t2;
  if ($[0] !== languageDefinition.languageName) {
    t2 = ["monaco-editor", languageDefinition.languageName];
    $[0] = languageDefinition.languageName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== languageDefinition) {
    t3 = () => initMonaco(languageDefinition);
    $[2] = languageDefinition;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: monaco,
    isLoading,
    error
  } = useQuery(t4);
  const t5 = monaco ?? null;
  const t6 = canEdit ? isLoading : false;
  const t7 = error as Error | null;
  let t8;
  if ($[7] !== languageDefinition || $[8] !== t5 || $[9] !== t6 || $[10] !== t7) {
    t8 = {
      monaco: t5,
      isLoading: t6,
      error: t7,
      editorOptions,
      languageSpec: languageDefinition
    };
    $[7] = languageDefinition;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== children || $[13] !== t8) {
    t9 = <MonacoContext.Provider value={t8}>{children}</MonacoContext.Provider>;
    $[12] = children;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
export const useMonaco = () => {
  const context = useContext(MonacoContext);
  if (!context) {
    throw new Error("useMonaco must be used within a MonacoProvider");
  }
  return context;
};